<template>
  <MTPage>
    <div class="max-w-4xl mx-auto space-y-6">

      <!-- Hero Section -->
      <div class="bg-[var(--mt-card-bg)] rounded-2xl shadow-sm border border-[var(--mt-border)] p-4">
        <div class="flex items-center justify-between gap-3 mb-2">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center" :style="{ background: 'var(--mt-accent-purple-muted, var(--mt-accent-blue-muted))' }">
              <MTIcon name="brain" :size="22" :style="{ color: 'var(--mt-accent-purple, var(--mt-accent-blue))' }" />
            </div>
            <h1 class="text-xl sm:text-2xl font-extrabold text-[var(--mt-text)]">Revision Quiz</h1>
          </div>
          <MTButton variant="outline" size="sm" @click="goDashboard">Back</MTButton>
        </div>
        <div class="flex items-center justify-between gap-3">
          <p class="text-xs text-[var(--mt-text-muted)] font-medium">
            Answer all questions first, then see your results
            <template v-if="dueCount > 0">
              <span class="opacity-40 mx-1">&middot;</span>
              {{ dueCount }} skill{{ dueCount > 1 ? 's' : '' }} due for revision
            </template>
          </p>
          <span v-if="phase === 'quiz'" class="text-xs font-bold text-[var(--mt-text-muted)]">
            {{ engine.answeredCount.value }}/{{ engine.queue.value.length }} answered
          </span>
        </div>
      </div>

      <!-- ============ CONFIG PHASE ============ -->
      <MTCard v-if="phase === 'config'" class="p-6 text-center">
        <div class="flex justify-center mb-3">
          <div class="w-16 h-16 rounded-full flex items-center justify-center" :style="{ background: 'var(--mt-icon-blue-bg)' }">
            <MTIcon name="brain" :size="32" :style="{ color: 'var(--mt-icon-blue-fg)' }" />
          </div>
        </div>

        <p class="text-lg font-semibold mb-2">Revision Quiz</p>
        <p class="text-[var(--mt-text-muted)] mb-4">
          Answer all questions without feedback, then review your results.
        </p>

        <!-- Question count selector -->
        <div class="mb-4">
          <label class="text-sm font-medium text-[var(--mt-text-muted)] mb-2 block">
            Number of questions
          </label>
          <div class="flex justify-center gap-3">
            <button
              v-for="count in questionCountOptions"
              :key="count"
              class="px-4 py-2 rounded-lg border-2 font-semibold text-sm transition-all"
              :class="selectedCount === count
                ? 'count-selected'
                : 'border-[var(--mt-border)] hover:border-[var(--mt-text-muted)] text-[var(--mt-text-muted)]'"
              @click="selectedCount = count"
            >
              {{ count }}
            </button>
          </div>
        </div>

        <MTButton variant="primary" size="lg" @click="startQuiz" :disabled="!progressStore.isLoaded(childId)">
          <MTIcon name="play-circle" :size="18" class="mr-2" />
          {{ progressStore.isLoaded(childId) ? 'Start Quiz' : 'Loading...' }}
        </MTButton>
      </MTCard>

      <!-- ============ LOADING PHASE ============ -->
      <MTCard v-else-if="phase === 'loading'" class="p-10 text-center">
        <div class="flex justify-center mb-4">
          <div class="w-12 h-12 rounded-full border-4 animate-spin spinner"></div>
        </div>
        <p class="text-lg font-semibold mb-1">Preparing your quiz...</p>
        <p class="text-sm text-[var(--mt-text-muted)]">
          Selecting questions based on your progress
        </p>
      </MTCard>

      <!-- ============ QUIZ PHASE ============ -->
      <template v-else-if="phase === 'quiz'">
        <MTCard class="p-6">
          <!-- Progress bar -->
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-[var(--mt-text-muted)]">
              {{ engine.answeredCount.value }} / {{ engine.queue.value.length }} answered
            </span>
            <span
              v-if="currentTopicLabel"
              class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[var(--mt-accent-blue-muted)] text-[var(--mt-accent-blue)]"
            >
              <MTIcon name="layers" :size="12" />
              {{ currentTopicLabel }}
            </span>
          </div>

          <div class="h-3 bg-[var(--mt-track-bg)] rounded-full overflow-hidden mb-4">
            <div
              class="h-3 transition-all duration-300 rounded-full"
              :style="{ background: 'var(--mt-accent-blue)', width: progressPercent + '%' }"
            ></div>
          </div>

          <!-- Dot indicators -->
          <div class="flex flex-wrap gap-2 mb-5">
            <button
              v-for="(_item, idx) in engine.queue.value"
              :key="idx"
              class="w-8 h-8 rounded-full border-2 text-sm font-medium transition-all flex items-center justify-center"
              :class="dotClass(idx)"
              @click="engine.goToQuestion(idx)"
              :title="'Question ' + (idx + 1)"
            >
              {{ idx + 1 }}
            </button>
          </div>

          <!-- Question area -->
          <div>
            <!-- Unanswered & not skipped: show interactive question -->
            <template v-if="!engine.isCurrentAnswered.value && !engine.isCurrentSkipped.value">
              <QuestionBlock
                v-if="engine.currentQuestion.value"
                :key="'q-' + engine.currentIndex.value + '-' + engine.currentQuestion.value._id"
                :question="engine.currentQuestion.value"
                :reviewMode="true"
                @questionCompleted="onQuestionCompleted"
              />
            </template>

            <!-- Already answered: show read-only review (no correct/wrong yet) -->
            <template v-else-if="engine.isCurrentAnswered.value">
              <div class="mt-card p-4 answered-card">
                <div class="flex items-center gap-2 mb-3">
                  <MTIcon name="lock" :size="16" :style="{ color: 'var(--mt-accent-blue)' }" />
                  <span class="text-sm font-medium" :style="{ color: 'var(--mt-accent-blue)' }">Answer locked</span>
                </div>
                <RevisionQuestionReview
                  :containerResults="engine.results[engine.currentIndex.value]?.containers || []"
                  :sectionGroupId="engine.results[engine.currentIndex.value]?.sectionGroupId || ''"
                  :studentId="childId"
                  :showFeedback="false"
                />
              </div>
            </template>

            <!-- Skipped: show option to answer -->
            <template v-else-if="engine.isCurrentSkipped.value">
              <div class="text-center py-8">
                <MTIcon name="skip-forward" :size="40" class="text-yellow-400 mx-auto mb-3" />
                <p class="text-lg font-semibold mb-2">You skipped this question</p>
                <p class="text-[var(--mt-text-muted)] mb-4">Want to give it a try?</p>
                <MTButton variant="primary" @click="answerSkipped">
                  Answer Now
                </MTButton>
              </div>
            </template>
          </div>

          <!-- Navigation bar -->
          <div class="mt-6 flex items-center justify-between border-t pt-4">
            <MTButton
              variant="outline"
              :disabled="engine.currentIndex.value === 0"
              @click="engine.goBack()"
            >
              <MTIcon name="chevron-left" :size="16" class="mr-1" />
              Back
            </MTButton>

            <div class="flex items-center gap-2">
              <MTButton
                v-if="!engine.isCurrentAnswered.value"
                variant="outline"
                @click="engine.skipQuestion()"
              >
                Skip
              </MTButton>

              <MTButton
                v-if="engine.allQuestionsHandled.value"
                variant="primary"
                @click="goToResults"
              >
                Finish Quiz
              </MTButton>

              <MTButton
                v-else
                variant="outline"
                :disabled="engine.currentIndex.value >= engine.queue.value.length - 1"
                @click="engine.goNext()"
              >
                Next
                <MTIcon name="chevron-right" :size="16" class="ml-1" />
              </MTButton>
            </div>
          </div>
        </MTCard>
      </template>

      <!-- ============ RESULTS PHASE ============ -->
      <template v-else-if="phase === 'results'">
        <RevisionResultsCard
          :results="engine.results"
          :score="engine.score.value"
          :topicBreakdown="engine.topicBreakdown.value"
          :childId="childId"
          :skippedCount="engine.skipped.size"
          :studentId="childId"
          :queue="engine.queue.value"
          :skipped="engine.skipped"
          @finish="onFinish"
          @retry="onRetry"
        />
      </template>

    </div>
  </MTPage>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { onBeforeRouteLeave } from "vue-router";
import { useAnalytics } from "@/composables/useAnalytics";

import MTPage from "@/components/ui/MTPage.vue";
import MTCard from "@/components/ui/MTCard.vue";
import MTButton from "@/components/ui/MTButton.vue";

import MTIcon from "@/components/ui/MTIcon.vue";

import QuestionBlock from "@/components/questions/QuestionBlock.vue";
import RevisionQuestionReview from "@/components/revision/RevisionQuestionReview.vue";
import RevisionResultsCard from "@/components/revision/RevisionResultsCard.vue";

import { useRevisionEngine } from "@/composables/useRevisionEngine";
import { useProgressStore } from "@/stores/progressStore";
import { useCurriculumStore } from "@/stores/curriculumStore";
import { usePlanLimits } from "@/composables/usePlanLimits";

const router = useRouter();
const route = useRoute();
const progressStore = useProgressStore();
const curriculumStore = useCurriculumStore();
const planLimits = usePlanLimits();

const childId = route.params.studentId;
const engine = useRevisionEngine(childId);
const { track } = useAnalytics();

const phase = ref("config"); // 'config' | 'quiz' | 'results' | 'loading'
const questionCountOptions = [10, 15, 20];
const selectedCount = ref(progressStore.getRevisionQuestionCount(childId));

const dueCount = computed(() => engine.dueRevisionCount(childId));

const currentTopicLabel = computed(() => {
  const sgId = engine.currentSectionGroupId?.value;
  if (!sgId) return '';
  const sg = curriculumStore.allSectionGroups.find(
    s => s.sectionGroupId === sgId
  );
  // allSectionGroups uses subsection.name; progress uses subsection.content — check both
  if (sg) return sg.sectionGroupTitle;
  for (const mod of curriculumStore.modules || []) {
    for (const unit of mod.units) {
      for (const lesson of unit.lessons) {
        const sub = (lesson.subsections || []).find(s => s.content === sgId);
        if (sub) return sub.title || sgId;
      }
    }
  }
  return sgId;
});

const progressPercent = computed(() => {
  const total = Math.max(1, engine.queue.value.length);
  return Math.round((engine.answeredCount.value / total) * 100);
});

async function startQuiz() {
  // Save preference only if changed
  if (selectedCount.value !== progressStore.getRevisionQuestionCount(childId)) {
    progressStore.setPreference(childId, { revisionQuestionCount: selectedCount.value });
  }

  phase.value = "loading";

  await engine.start({ totalQuestions: selectedCount.value });

  if (engine.queue.value.length > 0) {
    phase.value = "quiz";
    track('revision_started', {
      question_count: selectedCount.value,
      topics_available: engine.queue.value.length,
    });
  } else {
    phase.value = "config";
  }
}

function onQuestionCompleted(result) {
  engine.recordAnswer(result);
}

function answerSkipped() {
  // Remove from skipped so the interactive question renders
  engine.skipped.delete(engine.currentIndex.value);
}

function goToResults() {
  engine.finishSession();
  phase.value = "results";
  track('revision_completed', {
    questions_answered: engine.answeredCount.value,
    total_questions: engine.totalQuestions.value,
    score_percent: engine.score.value?.percent ?? null,
  });
}

onBeforeRouteLeave(() => {
  if (phase.value === 'quiz') {
    track('revision_abandoned', {
      questions_answered: engine.answeredCount.value,
      total_questions: engine.totalQuestions.value,
      progress_percent: engine.totalQuestions.value
        ? Math.round((engine.answeredCount.value / engine.totalQuestions.value) * 100)
        : 0,
    });
  }
});

function onFinish() {
  router.push(`/student/${childId}/dashboard`);
}

function onRetry() {
  engine.reset();
  phase.value = "config";
}

function goDashboard() {
  router.push(`/student/${childId}/dashboard`);
}

onMounted(async () => {
  await planLimits.loadPlans();
  if (!planLimits.isFeatureEnabled('revisionMode')) {
    // Plan does not include revision mode — redirect to student home
    router.replace(`/student/${childId}/dashboard`);
  }
});

// Dot indicator classes
function dotClass(idx) {
  const isCurrent = idx === engine.currentIndex.value;
  const isAnswered = idx in engine.results;
  const isSkipped = engine.skipped.has(idx);

  if (isCurrent) {
    if (isAnswered) return "border-green-500 bg-green-500 text-white dot-current-ring";
    if (isSkipped) return "border-yellow-500 bg-yellow-500 text-white dot-current-ring";
    return "dot-current";
  }
  if (isAnswered) return "border-green-500 bg-green-500 text-white";
  if (isSkipped) return "border-yellow-400 bg-yellow-400 text-white";
  return "border-[var(--mt-border)] bg-[var(--mt-card-bg)] text-[var(--mt-text-muted)]";
}

</script>

<style scoped>
.count-selected {
  border-color: var(--mt-accent-blue);
  background: var(--mt-accent-blue-muted);
  color: var(--mt-accent-blue);
}
.spinner {
  border-color: var(--mt-border);
  border-top-color: var(--mt-accent-blue);
}
.answered-card {
  background: var(--mt-accent-blue-muted);
  border-color: var(--mt-accent-blue);
}
.dot-current {
  border-color: var(--mt-accent-blue);
  background: var(--mt-accent-blue-muted);
  color: var(--mt-accent-blue);
  box-shadow: 0 0 0 2px var(--mt-accent-blue-muted);
}
.dot-current-ring {
  box-shadow: 0 0 0 2px var(--mt-accent-blue-muted);
}
</style>
